/* eslint-disable @typescript-eslint/no-unused-vars */
import { isEmpty } from 'lodash-es';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeInitialAcc, convertTargetsToTargetsWithTz, toAlertPayload } from '.';
import { useClient } from '../../ClientProvider';
import queryClient from '../../queryClient';
import { getPredicateKey } from '../../resources/alerts.types';
import useMutation from '../../useMutation';
import useQuery from '../../useQuery';
import { convertTargetsWithTzToTargets, getAlertSources } from '../alerts/alerts';
import useGetCompleteAlertTargets from './useGetCompleteAlertTargets';
const useAlertDetails = (alertId) => {
    const client = useClient();
    const profileRequest = useQuery(client.me.getMyProfile());
    const preferredUnits = profileRequest.data ? profileRequest.data.units : undefined;
    const getAlertRequester = client.alerts.get(preferredUnits)(alertId);
    const replaceAlertRequester = client.alerts.replace(preferredUnits)(alertId);
    // Handling alert deletion
    const [deleteAlert, deleteAlertRequest] = useMutation(client.alerts.delete(alertId), {
        keysToInvalidate: [client.alerts.getMany(preferredUnits).invalidationKey],
        onSuccess: () => {
            queryClient.removeQueries({
                predicate: ({ queryKey }) => {
                    return queryKey[0] === getAlertRequester.key;
                }
            });
        }
    });
    // Fetching alert
    const alertRequest = useQuery(getAlertRequester, {
        enabled: !isNil(alertId) && !(deleteAlertRequest.isPending || deleteAlertRequest.isSuccess)
    });
    const alert = alertRequest.data;
    const { completeTargets, isLoading: completeAlertIsLoading } = useGetCompleteAlertTargets(alert?.targets, []);
    const isLoading = completeAlertIsLoading || alertRequest.isLoading;
    const [updatedAlert, setUpdatedAlert] = useState(alert);
    // Predicate Edition
    const [editedPredicate, setEditedPredicate] = useState(undefined);
    // Target Edition
    const [selectedTzTargets, setSelectedTzTargets] = useState();
    const [isTouched, setIsTouched] = useState(false);
    const sources = useMemo(() => (!isNil(updatedAlert) ? getAlertSources(updatedAlert) : []), [updatedAlert]);
    useEffect(() => {
        if (alert && !isLoading && isEmpty(selectedTzTargets) && !isEmpty(completeTargets)) {
            const targetWithTz = convertTargetsToTargetsWithTz({
                completeTargets,
                targetsToConvert: alert.targets
            });
            if (!isEqual(selectedTzTargets, targetWithTz)) {
                setSelectedTzTargets(targetWithTz);
            }
        }
    }, [completeTargets, alert, isLoading, selectedTzTargets]);
    // Handling alert update
    const [replaceAlert, replaceAlertRequest] = useMutation(replaceAlertRequester, {
        onSuccess: () => {
            setIsTouched(false);
        },
        keysToInvalidate: [
            client.alerts.getMany(preferredUnits).invalidationKey,
            client.alerts.get(preferredUnits)(alertId).invalidationKey
        ]
    });
    /** Predicates Callbacks */
    const handlePredicateEdition = useCallback((predicateIdx) => {
        if (updatedAlert) {
            const keyToUpdate = Object.keys(updatedAlert.predicates)[predicateIdx];
            if (!isNil(keyToUpdate)) {
                setEditedPredicate(updatedAlert.predicates[keyToUpdate]);
            }
        }
    }, [updatedAlert, setEditedPredicate]);
    const handlePredicateDeletion = useCallback((predicateIdx) => {
        if (updatedAlert) {
            const keyToDelete = Object.keys(updatedAlert.predicates)[predicateIdx];
            if (!isNil(keyToDelete)) {
                const newPredicates = { ...updatedAlert.predicates };
                delete newPredicates[keyToDelete];
                const alertToUpdate = { ...updatedAlert, predicates: newPredicates };
                setUpdatedAlert(alertToUpdate);
            }
        }
    }, [updatedAlert, setUpdatedAlert]);
    const handlePredicateAddition = useCallback(() => {
        setEditedPredicate(undefined);
    }, []);
    /** Alert Callbacks */
    const handleTargetDeletion = useCallback((targets) => {
        const alertToUpdate = {
            ...updatedAlert,
            targets: convertTargetsWithTzToTargets(targets)
        };
        setSelectedTzTargets(targets);
        setUpdatedAlert(alertToUpdate);
    }, [updatedAlert, setUpdatedAlert]);
    const handleTargetEditionConfirmation = useCallback((targets) => {
        if (selectedTzTargets == undefined) {
            throw new Error('selectedTzTargets should ne be empty here');
        }
        const alertToUpdate = {
            ...updatedAlert,
            targets: convertTargetsWithTzToTargets(targets)
        };
        setSelectedTzTargets(targets);
        setUpdatedAlert(alertToUpdate);
    }, [updatedAlert, selectedTzTargets]);
    const handleUpdate = useCallback(async () => {
        if (selectedTzTargets === undefined) {
            throw new Error('selectedTzTargets should not be undefined');
        }
        if (updatedAlert) {
            const payload = await computeInitialAcc(toAlertPayload(updatedAlert), selectedTzTargets, client);
            replaceAlert(payload);
        }
    }, [updatedAlert, selectedTzTargets, client, replaceAlert]);
    const handleFormChange = ({ newAlert, isValid }) => {
        setUpdatedAlert(newAlert);
        setIsTouched(isValid);
    };
    const handlePredicateEditionValidation = useCallback((finalPredicate) => {
        if (!isNil(editedPredicate)) {
            // removes the edited predicates and adds a new one
            const updatePredicates = ({ [getPredicateKey(editedPredicate)]: _, ...updatedPredicates }) => ({
                ...updatedPredicates,
                [getPredicateKey(finalPredicate)]: finalPredicate
            });
            const newAlert = {
                ...updatedAlert,
                predicates: !isNil(updatedAlert) ? updatePredicates(updatedAlert.predicates) : {}
            };
            setUpdatedAlert(newAlert);
        }
        else {
            const newPredicates = {
                ...updatedAlert?.predicates,
                [getPredicateKey(finalPredicate)]: finalPredicate
            };
            const newAlert = {
                ...updatedAlert,
                predicates: newPredicates
            };
            setUpdatedAlert(newAlert);
        }
        setEditedPredicate(undefined);
    }, [editedPredicate, updatedAlert]);
    // workaround to avoid empty/default state on page load, alert shouldn't change after loading so it should be fine
    useEffect(() => {
        if (alert) {
            setUpdatedAlert(alert);
        }
    }, [alert]);
    useEffect(() => {
        const targetsChanged = updatedAlert && alert && alert.targets !== updatedAlert.targets;
        const predicatesChanged = updatedAlert && alert && alert.predicates !== updatedAlert.predicates;
        const nonObjFieldChanged = updatedAlert &&
            alert &&
            ['isVoiceCallEnabled', 'isActive', 'name'].some((field) => alert[field] !== updatedAlert[field]);
        const alertHasChanged = targetsChanged || predicatesChanged || nonObjFieldChanged;
        if (alertHasChanged) {
            setIsTouched(alertHasChanged);
        }
    }, [updatedAlert, alert]);
    return {
        isLoading,
        alert,
        alertRequest,
        replaceAlert,
        deleteAlert,
        editedPredicate,
        handleFormChange,
        handlePredicateAddition,
        handlePredicateDeletion,
        handlePredicateEdition,
        handlePredicateEditionValidation,
        handleTargetDeletion,
        handleTargetEditionConfirmation,
        handleUpdate,
        isTouched,
        replaceAlertRequest,
        sources,
        updatedAlert,
        selectedTzTargets
    };
};
export default useAlertDetails;
